'use client';

import React from 'react';
import { Box } from '@unity/cloud-ui';
import { useTheme } from '@unity/cloud-ui/styles';
import z from 'zod';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';
import { customPalette } from '@/components/theme/custom-palette';

const phosphorWeights = ['thin', 'light', 'regular', 'bold', 'fill', 'duotone'] as const;

/**
 * In order to avoid to load the whole Phoshphor library on the client (~6mb)
 * with this: `import * as PhosphorIcons from '@phosphor-icons/react';`
 *
 * Icons svg content are injected by the server using the remark plugin
 * `remark-phosphor-icons` using the `name` prop, all the icon props icon
 * are passed to it when generating the svg content.
 *
 * Component APIs is not altered from the tech-writer pov, just we techinally
 * transform it on the server.
 */

export const Icon = (props: z.input<typeof iconSchema>) => {
  const { palette } = useTheme();

  if (!props.name) {
    return null;
  }

  return (
    <Box
      color={palette.primary.main}
      dangerouslySetInnerHTML={{
        __html: props.name,
      }}
    />
  );
};

const documentationURL = getComponentDocumentationUrl('icons');

const descriptions = {
  name: 'The name of the icon from [Phosphor icons](https://phosphoricons.com/).',
  color: 'The color of the icon.',
  weight: 'The weight of the icon.',
  size: 'The size of the icon.',
  schema: `Display an icon from [Phosphor icons](https://phosphoricons.com/).\n\n\n**Example**\n\`\`\`mdx\n<Icon\n  name="airplane"\n  color="blue"\n  weight="bold"\n  size={24}\n/>\n\`\`\`\n\n${documentationURL}`,
};

export const iconSchema = z
  .object({
    name: z.string().describe(descriptions.name),
    color: z
      .enum(Object.keys(customPalette.light.icons) as [string, ...string[]])
      .describe(descriptions.color)
      .optional(),
    weight: z.enum(phosphorWeights).describe(descriptions.weight).prefault('regular').optional(),
    size: z.number().describe(descriptions.size).prefault(16).optional(),
  })
  .describe(descriptions.schema);
